import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, X } from 'lucide-react';
import toast from 'react-hot-toast';

import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import { TablePagination } from '@/components/shared/TablePagination';
import { TableActionsMenu } from '@/components/shared/TableActionsMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { createInvitation } from '@/services/invitationService';
import { listUsersPaginated, cancelInvitation } from '@/services/userService';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import { useAuth } from '@/hooks/useAuth';
import type { User, UserRole } from '@packages/shared-types';

// Root context schema (for root users not impersonating)
const rootInvitationSchema = z.object({
  email: z.string().email('validation.emailInvalid'),
  role: z.enum(['root', 'admin'] as const),
});

// Tenant context schema (for tenant admins or root users impersonating)
const tenantInvitationSchema = z.object({
  email: z.string().email('validation.emailInvalid'),
  role: z.enum(['admin', 'user'] as const),
});

type RootInvitationFormValues = z.infer<typeof rootInvitationSchema>;
type TenantInvitationFormValues = z.infer<typeof tenantInvitationSchema>;
type InvitationFormValues = RootInvitationFormValues | TenantInvitationFormValues;

export function UsersPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTenant } = useTenantSwitcher();
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);

  const pagination = usePaginationFromUrl();

  // Detect if we're in tenant context
  const isTenantContext = Boolean(activeTenant) || user?.role !== 'root';
  const invitationSchema = isTenantContext
    ? tenantInvitationSchema
    : rootInvitationSchema;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InvitationFormValues>({
    resolver: zodResolver(invitationSchema),
    defaultValues: {
      email: '',
      role: 'admin',
    },
  });

  const fetchUsers = useCallback(async (): Promise<void> => {
    setLoadingUsers(true);
    try {
      const result = await listUsersPaginated({
        page: pagination.page,
        limit: pagination.limit,
        tenantId: activeTenant?.id,
      });
      const filtered = result.data.filter(
        (user) => !user.email.includes('change-me-initial-setup'),
      );
      setUsers(filtered);
      pagination.setTotal(result.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUsers(false);
    }
  }, [activeTenant, pagination.page, pagination.limit]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const statusLabel = useMemo(
    () => ({
      pending: t('pages.users.invites.status.pending'),
      accepted: t('pages.users.invites.status.accepted'),
    }),
    [t],
  );

  const getUserDisplayName = useCallback((user: User): string => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    return user.email;
  }, []);

  const getInviterDisplayName = useCallback((user: User): string => {
    if (!user.inviter) {
      return '-';
    }
    const { inviter } = user;
    if (inviter.firstName && inviter.lastName) {
      return `${inviter.firstName} ${inviter.lastName}`;
    }
    if (inviter.firstName) {
      return inviter.firstName;
    }
    return inviter.email;
  }, []);

  const onSubmit = async (values: InvitationFormValues): Promise<void> => {
    setFormError(null);
    try {
      await createInvitation(
        {
          email: values.email,
          role: values.role as UserRole,
        },
        activeTenant?.id,
      );
      toast.success(t('pages.users.invites.success'));
      reset({
        email: '',
        role: 'admin',
      });
      setIsInviteDialogOpen(false);
      await fetchUsers();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('pages.users.invites.error');
      setFormError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleCancelInvitationClick = useCallback((user: User) => {
    setSelectedUser(user);
    setIsCancelDialogOpen(true);
  }, []);

  const handleCancelInvitation = useCallback(async () => {
    if (!selectedUser) return;
    setIsCanceling(true);
    try {
      await cancelInvitation(selectedUser.id, activeTenant?.id);
      await fetchUsers();
      setIsCancelDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      console.error(err);
    } finally {
      setIsCanceling(false);
    }
  }, [selectedUser, activeTenant, fetchUsers]);

  const isPending = useCallback((user: User) => Boolean(user.invitationToken), []);

  const handleEditClick = useCallback(
    (userId: string) => {
      return () => navigate(`/users/${userId}`);
    },
    [navigate],
  );

  const handleCancelInvitationClickCallback = useCallback(
    (user: User) => {
      return () => handleCancelInvitationClick(user);
    },
    [handleCancelInvitationClick],
  );

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('pages.users.title')}
        description={t(
          isTenantContext
            ? 'pages.users.descriptionTenant'
            : 'pages.users.description',
        )}
        actions={
          <Dialog
            open={isInviteDialogOpen}
            onOpenChange={(open) => {
              setIsInviteDialogOpen(open);
              if (open) {
                // Reset form when opening to ensure correct schema is used
                reset({
                  email: '',
                  role: 'admin',
                });
              } else {
                setFormError(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm">{t('pages.users.actions.invite')}</Button>
            </DialogTrigger>
          <DialogContent className="w-full max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('pages.users.invites.title')}</DialogTitle>
              <DialogDescription>
                {t(
                  isTenantContext
                    ? 'pages.users.invites.descriptionTenant'
                    : 'pages.users.invites.description',
                )}
              </DialogDescription>
            </DialogHeader>
            <form
              id="invite-form"
              className="space-y-4"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {t('pages.users.invites.form.email')}
                  </Label>
                  <Input
                    id="email"
                    placeholder="name@trushelf.io"
                    {...register('email')}
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive">
                      {t(errors.email.message as string)}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">
                    {t('pages.users.invites.form.role')}
                  </Label>
                  <Controller
                    control={control}
                    name="role"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger id="role">
                          <SelectValue placeholder={t('pages.users.invites.form.selectRole')} />
                        </SelectTrigger>
                        <SelectContent>
                          {isTenantContext ? (
                            <>
                              <SelectItem value="admin">
                                {t('pages.users.invites.roles.administrator')}
                              </SelectItem>
                              <SelectItem value="user">
                                {t('pages.users.invites.roles.user')}
                              </SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="root">
                                {t('pages.users.invites.roles.root')}
                              </SelectItem>
                              <SelectItem value="admin">
                                {t('pages.users.invites.roles.support')}
                              </SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.role && (
                    <p className="text-xs text-destructive">
                      {t('pages.users.invites.form.roleError')}
                    </p>
                  )}
                </div>
              </div>
              {formError && (
                <p className="text-sm text-destructive">{formError}</p>
              )}
            </form>
            <DialogFooter className="pt-0">
              <Button
                variant="ghost"
                onClick={() => setIsInviteDialogOpen(false)}
                disabled={isSubmitting}
              >
                {t('pages.users.actions.cancel')}
              </Button>
              <Button
                type="submit"
                form="invite-form"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t('pages.users.invites.form.submitting')
                  : t('pages.users.invites.form.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        }
      />
      <TableCard>
        <>
        <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('pages.users.invites.table.name')}</TableHead>
                <TableHead>{t('pages.users.invites.table.email')}</TableHead>
                <TableHead>{t('pages.users.invites.table.role')}</TableHead>
                <TableHead>{t('pages.users.invites.table.status')}</TableHead>
                <TableHead>{t('pages.users.invites.table.invitedBy')}</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingUsers ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8">
                    <div className="flex flex-col items-center gap-3">
                      <LoadingLogo size="sm" />
                      <span className="text-sm text-muted-foreground">
                        {t('pages.users.invites.listLoading')}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-sm text-muted-foreground"
                  >
                    {t(
                      isTenantContext
                        ? 'pages.users.invites.listEmptyTenant'
                        : 'pages.users.invites.listEmpty',
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    onRowClick={() => navigate(`/users/${user.id}`)}
                  >
                    <TableCell className="font-medium">
                      {getUserDisplayName(user)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.email}
                    </TableCell>
                    <TableCell className="uppercase tracking-wide text-muted-foreground">
                      {t(`pages.users.invites.roles.${user.role}`, {
                        defaultValue: user.role,
                      })}
                    </TableCell>
                    <TableCell>
                      {isPending(user) ? (
                        <StatusBadge variant="warning">
                          {statusLabel.pending}
                        </StatusBadge>
                      ) : (
                        <StatusBadge variant="success">
                          {statusLabel.accepted}
                        </StatusBadge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {getInviterDisplayName(user)}
                    </TableCell>
                    <TableCell>
                      <TableActionsMenu
                        actions={
                          !isPending(user)
                            ? [
                                {
                                  label: t('pages.users.actions.edit'),
                                  icon: Pencil,
                                  onClick: handleEditClick(user.id),
                                },
                              ]
                            : [
                                {
                                  label: t('pages.users.actions.cancelInvitation'),
                                  icon: X,
                                  onClick: handleCancelInvitationClickCallback(user),
                                },
                              ]
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
            pageOfLabel={t('pages.common.pagination.pageOf', {
              current: pagination.page,
              total: pagination.totalPages,
            })}
            onRefresh={() => void fetchUsers()}
            isRefreshing={loadingUsers}
          />
        </>
      </TableCard>

      {/* Cancel Invitation Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('pages.users.dialogs.cancelInvitation.title')}
            </DialogTitle>
            <DialogDescription>
              {t('pages.users.dialogs.cancelInvitation.description', {
                email: selectedUser?.email ?? '',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCancelDialogOpen(false);
                setSelectedUser(null);
              }}
            >
              {t('pages.users.actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancelInvitation}
              disabled={isCanceling}
            >
              {t('pages.users.actions.cancelInvitation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
